import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Snackbar, Alert, AlertColor, Slide } from '@mui/material';

/**
 * Notifications applicatives.
 *
 * Remplace les appels a alert(), qui bloquent le fil d'execution, ignorent le
 * theme de l'application et sont filtres par certains navigateurs mobiles.
 */

interface ToastState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

interface ToastContextType {
  showToast: (message: string, severity?: AlertColor) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tr } = useAppTranslation();
  const [state, setState] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showToast = useCallback((message: string, severity: AlertColor = 'info') => {
    setState({ open: true, message, severity });
  }, []);

  const valeur = useMemo<ToastContextType>(() => ({
    showToast,
    success: (m: string) => showToast(m, 'success'),
    error: (m: string) => showToast(m, 'error'),
    info: (m: string) => showToast(m, 'info'),
    warning: (m: string) => showToast(m, 'warning'),
  }), [showToast]);

  const fermer = (_?: unknown, raison?: string) => {
    // Un clic a cote ne doit pas escamoter un message d'erreur que
    // l'utilisateur n'a peut-etre pas encore lu.
    if (raison === 'clickaway') return;
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <ToastContext.Provider value={valeur}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.severity === 'error' ? 8000 : 4000}
        onClose={fermer}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={fermer}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, borderRadius: 2, boxShadow: 6 }}
        >
          {tr(state.message)}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error('useToast doit être utilisé à l\'intérieur d\'un ToastProvider');
  }
  return ctx;
};
